/**
 * Curated podcast-style narration scripts — Estimation pages.
 * Keyed by ListenButton guideId. See index.js for the writing style guide.
 */

export const ESTIMATION_NARRATIONS = {
  // -------------------------------------------------------------------------
  // Cost Breakdown v2
  // -------------------------------------------------------------------------

  'cb-readme-help': [
    {
      title: 'Welcome to the Cost Breakdown',
      icon: '📖',
      turns: [
        { speaker: 'clara', speech: `Welcome! This is the read-me for the whole Cost Breakdown — built natively in the portal, the same flow as Casper's workbook, without juggling an Excel file for every revision.` },
        { speaker: 'liam', speech: `You pick one estimate, fill the sheets in order, and the portal keeps the math honest. No mystery import step. What you type is what rolls.` },
      ],
    },
    {
      title: 'One estimate to rule them all',
      icon: '📕',
      turns: [
        { speaker: 'clara', speech: `The portal shares one list of estimates. The same estimate that owns your M T O also owns its cost breakdown.` },
        { speaker: 'liam', speech: `Choose it from the top bar or the Estimate Log, and every sheet you open — detail, summary, rentals, one-pager — scopes to that job.` },
      ],
    },
    {
      title: 'Numbers flow uphill',
      icon: '⛰️',
      turns: [
        { speaker: 'clara', speech: `Think of a pyramid. Detailed Estimation is the base — one row per piece of scope. Discipline Summaries sit in the middle for labour planning.` },
        { speaker: 'liam', speech: `Lookups price the hours and equipment. Indirects, site, rentals, and crew assemble the package. C B Summary is the live one-pager. S O V speaks the client's language last.` },
      ],
    },
    {
      title: 'What we proved on a real job',
      icon: '✅',
      turns: [
        { speaker: 'clara', speech: `We checked a large real SharePoint cost breakdown — estimate twenty six U S one zero three five — reading Excel Detail totals against Summary totals.` },
        { speaker: 'liam', speech: `Direct labour lined up. Staff and supervision stay on Summary on purpose. Materials and rentals often grow on Summary because of taxes, duties, and equipment lines that never lived on Detail. That is Casper's design, and the portal follows it.` },
      ],
    },
    {
      title: 'Libraries versus estimate data',
      icon: '📚',
      turns: [
        { speaker: 'clara', speech: `Lookup Tables and Rates hold the shared libraries. Import a copy into your estimate, then tailor freely.` },
        { speaker: 'liam', speech: `Your edits never rewrite the library, and a library update never silently reprices a bid you already closed. Clear the estimate selection when you just want to maintain the bank.` },
      ],
    },
    {
      title: 'You are ready',
      icon: '🚀',
      turns: [
        { speaker: 'clara', speech: `One estimate. Detail into summary into assembly into the one-pager, then S O V and the bid package.` },
        { speaker: 'liam', speech: `Every sheet has its own help with an audio guide like this one. Happy estimating!` },
      ],
    },
  ],

  'cb-workflow-help': [
    {
      title: 'The estimating playbook',
      icon: '📋',
      turns: [
        { speaker: 'clara', speech: `This is the step-by-step playbook — from first look at scope all the way to the client's schedule of values.` },
        { speaker: 'liam', speech: `It mirrors Casper's W I P cost breakdown: detail, then summary, then assembly, then the one-pager, then client S O V.` },
      ],
    },
    {
      title: 'Leads set the table',
      icon: '👔',
      turns: [
        { speaker: 'clara', speech: `Early steps belong to the lead: scope, terms, self-perform versus subcontract, taxes, duties, and where material and fab come from.` },
        { speaker: 'liam', speech: `Open the Estimate Log, pick the estimate, and keep SharePoint handy for drawings and quotes.` },
      ],
    },
    {
      title: 'The team fills the sheets',
      icon: '👷',
      turns: [
        { speaker: 'clara', speech: `Ready the lookups, then enter takeoff in Detailed Estimation — piping through the M T O, other trades on their detail sheets.` },
        { speaker: 'liam', speech: `Hit roll up so Auto rows land on Discipline Summaries. Pull rentals into the estimate when equipment is priced. Suggest indirects, tune site expenses, pull crew hours from summaries.` },
      ],
    },
    {
      title: 'The lead closes it out',
      icon: '🏁',
      turns: [
        { speaker: 'clara', speech: `Open C B Summary for markups and, when the M T O rate is frozen, a U S dollar view. Sync S O V from W B S tags when the client structure is ready.` },
        { speaker: 'liam', speech: `Finish the Bid Package letters. Follow the sidebar top to bottom and you will not miss a bucket. Good luck with the bid!` },
      ],
    },
  ],

  'estimate-log-help': [
    {
      title: 'The master list',
      icon: '📕',
      speech: `This is the Estimate Log — the master list of every estimate in the portal. It's the same registry behind the Estimates dropdown in the top bar and the M T O module, so there's exactly one source of truth. No duplicate lists to keep in sync.`,
    },
    {
      title: 'Click to activate',
      icon: '👆',
      speech: `Clicking a row does something powerful: it makes that estimate active for the entire Cost Breakdown. Every discipline sheet, the indirects, the site expenses, and the one-pager will all scope themselves to it automatically. The active row is highlighted so you always know where you are.`,
    },
    {
      title: 'Live rollups and statuses',
      icon: '📈',
      speech: `The hours and cost columns are live rollups of what's been estimated so far — they update as the team works. Use the status dropdown to track the bid: draft, in progress, submitted, and eventually awarded or lost. Awarding itself happens in the admin's Estimate Management screen, because it also creates the project and pushes the M T O into the job tracker.`,
    },
    {
      title: 'Starting a new one',
      icon: '✨',
      speech: `To start a new estimate, click New Estimate, give it a number and a name, pick the client, division, and disciplines, and you're off — the portal drops you straight into its cost breakdown. That's it. Pick an estimate and let's build a price!`,
    },
  ],

  'cb-detail-help': [
    {
      title: 'The takeoff sheet',
      icon: '🔍',
      turns: [
        { speaker: 'clara', speech: `Welcome to Detailed Estimation — the bottom of the pyramid. Each row is one piece of scope: a tag, a pump, a steel assembly.` },
        { speaker: 'liam', speech: `You price hours, material, rentals, and subs on that one item, and the sheet keeps score the same way Casper's Detail tabs do.` },
      ],
    },
    {
      title: 'One sheet, twelve disciplines',
      icon: '🧩',
      turns: [
        { speaker: 'clara', speech: `It is the same sheet for every discipline. Column groups adapt — structural shows iron worker hours, mechanical shows millwright and vulcanizing.` },
        { speaker: 'liam', speech: `Behaviour stays identical everywhere. Learn it once, use it twelve times.` },
      ],
    },
    {
      title: 'The lightning columns',
      icon: '⚡',
      turns: [
        { speaker: 'clara', speech: `Lightning columns are calculated: total material, combined hours, factored hours, total sub cost. You cannot type into them.` },
        { speaker: 'liam', speech: `Set the labour hours factor to one hundred ten and every hour on that row grows by ten percent — a clean way to price difficulty.` },
      ],
    },
    {
      title: 'Roll it up — Casper exact',
      icon: '⬆️',
      turns: [
        { speaker: 'clara', speech: `Roll up mirrors Casper's Excel formulas. Materials become separate Auto lines — shims, grout, anchors, misc, epoxy — plus consumables at a percent of direct and other direct labour.` },
        { speaker: 'liam', speech: `Other misc rentals roll from Detail. Big equipment packages come from Pull Rentals. Sub trades, room and board, and site stay on Summary — in Excel those Detail columns are reverse allocations, not sources. Re-run anytime; Auto lines refresh.` },
      ],
    },
  ],

  'discipline-summary-help': [
    {
      title: 'The middle layer',
      icon: '📊',
      turns: [
        { speaker: 'clara', speech: `This is the Discipline Summary — the labour planning worksheet in the middle of the estimate.` },
        { speaker: 'liam', speech: `Below it is Detailed Estimation. Above it is the live C B Summary. Thirteen sections hold the complete cost picture for one trade.` },
      ],
    },
    {
      title: 'Three kinds of rows',
      icon: '🧬',
      turns: [
        { speaker: 'clara', speech: `Auto rows with account code A U T O come from Detail when you roll up or re-roll. Refresh them as often as the takeoff changes.` },
        { speaker: 'liam', speech: `Library rows bring standard support and supervision. Manual rows cover anything you price only at summary — taxes on materials, extra rental packages, room and board codes.` },
      ],
    },
    {
      title: 'Zero-cent parity — what belongs where',
      icon: '🧠',
      turns: [
        { speaker: 'clara', speech: `We traced a real SharePoint cost breakdown line by line. Material components from Detail match Summary to the cent. Consumables is four percent of direct plus other direct labour — same Excel formula.` },
        { speaker: 'liam', speech: `Equipment rentals come from the Rentals pull. Sub-trade packages, room and board, and site expenses are Summary work — priced from rates and the site sheet — not a blind sum of Detail. Fill those the Casper way and the one-pager lines up.` },
      ],
    },
    {
      title: 'Everything is live',
      icon: '🔄',
      turns: [
        { speaker: 'clara', speech: `Edit a cell and the cards at the top refresh. There is no separate sync step — this sheet already feeds the one-pager.` },
        { speaker: 'liam', speech: `If a number looks wrong upstairs, start here. Happy planning!` },
      ],
    },
  ],

  'cb-one-pager-help': [
    {
      title: 'The money page',
      icon: '📄',
      turns: [
        { speaker: 'clara', speech: `This is the C B Summary — the one-pager you would hand to management. Rows are cost types, columns are disciplines.` },
        { speaker: 'liam', speech: `The bottom-right corner is the number everyone cares about. Nothing here is a stale stored copy.` },
      ],
    },
    {
      title: 'Computed, never stored',
      icon: '🧮',
      turns: [
        { speaker: 'clara', speech: `Every cell is computed live from discipline summaries, indirects, and site expenses when you open the page.` },
        { speaker: 'liam', speech: `Change a sheet below and this page moves with it. That is why the chain of truth matters.` },
      ],
    },
    {
      title: 'Markups and U S dollars',
      icon: '💹',
      turns: [
        { speaker: 'clara', speech: `Set markup percent by cost type — maybe ten on labour, five on materials, fifteen on subs — and watch selling price and margin update.` },
        { speaker: 'liam', speech: `When the estimate has a frozen M T O exchange rate, toggle U S dollars for a converted view. No frozen rate means stay in Canadian dollars until the rate is locked.` },
      ],
    },
    {
      title: 'Follow the numbers down',
      icon: '🕵️',
      turns: [
        { speaker: 'clara', speech: `Surprised by a cell? Drill into that discipline's summary, then into Detail.` },
        { speaker: 'liam', speech: `One-pager, summary, detail — three levels, one chain. Go get the bid!` },
      ],
    },
  ],

  'cb-indirects-help': [
    {
      title: 'The people around the work',
      icon: '👥',
      speech: `Indirects are everyone who supports the job without swinging a wrench: project managers, coordinators, quality control, safety, warehousing — plus their trucks. They're priced per estimate on this sheet, separately from the discipline labour.`,
    },
    {
      title: 'Three phases per position',
      icon: '📅',
      speech: `Each row covers one position across three phases: pre-site, at site, and post-site. For each phase you set weeks, hours per week, and a blended rate, and the grand totals fall out automatically. Vehicles ride along on the same row — quantity, months, and cost per month.`,
    },
    {
      title: 'Start from the library',
      icon: '📚',
      speech: `Don't build this from scratch. Import from Library pulls in L G L's standard indirect positions, and then you tune the weeks and rates to this project. One thing people miss: check for overlap with the support staff sections in the discipline summaries, so nobody gets priced twice.`,
    },
    {
      title: 'Where it lands',
      icon: '🎯',
      speech: `The sheet's grand totals feed the Indirects line of the C B Summary — estimate wide, not per discipline. Set it up once, keep it honest, and the one-pager takes care of the rest.`,
    },
  ],

  'site-expenses-detail-help': [
    {
      title: 'The cost of being there',
      icon: '🏗️',
      speech: `Site expenses are the recurring costs of simply running the site: office trailers, tool cribs, internet, utilities, waste bins, consumable distribution. Individually small, collectively very real — this sheet keeps them from slipping through.`,
    },
    {
      title: 'Quantity, duration, rate',
      icon: '✖️',
      speech: `Every line follows the same simple math: quantity, times duration, times rate. Two trailers, for ten months, at fifteen hundred a month. Enter the pieces and the total, tag the line to a trade if it belongs to one, and move on.`,
    },
    {
      title: 'The standard checklist',
      icon: '📚',
      speech: `The library holds the standard site expense checklist — import it at the start of every estimate and you'll never forget the porta-johns again. Then delete what doesn't apply and adjust the rest. The sheet total flows straight into the Site Expenses line of the C B Summary.`,
    },
  ],

  'crew-calculation-bank-help': [
    {
      title: 'People versus time',
      icon: '👷',
      turns: [
        { speaker: 'clara', speech: `Crew calculations answer one question: how many people, for how long?` },
        { speaker: 'liam', speech: `Take a discipline's man hours, pick hours per day and days per week, and each row becomes a crew-size scenario with a duration.` },
      ],
    },
    {
      title: 'Pull hours from summaries',
      icon: '🔗',
      turns: [
        { speaker: 'clara', speech: `Pull man hours from summaries fills the hour column from Discipline Summary labour — the same hours that already priced the bid.` },
        { speaker: 'liam', speech: `You still choose crew size and schedule. The pull is tagged so you can refresh without losing your scenario choices.` },
      ],
    },
    {
      title: 'What-if in rows',
      icon: '🔀',
      turns: [
        { speaker: 'clara', speech: `Compare scenarios side by side. Eight pipe fitters for twenty weeks, or twelve for thirteen?` },
        { speaker: 'liam', speech: `Keep the hours the same and let the durations argue it out before you commit to a bid duration.` },
      ],
    },
    {
      title: 'Library templates',
      icon: '📚',
      turns: [
        { speaker: 'clara', speech: `Import a typical crew setup from the library when you want a starting point.` },
        { speaker: 'liam', speech: `Tune it, then carry the answer into scheduling. Simple, honest math.` },
      ],
    },
  ],

  'clarifications-help': [
    {
      title: 'The fine print that saves you',
      icon: '📝',
      speech: `Clarifications are the fine print of the bid: what you assumed, what's included, and — crucially — what's excluded. Months later, when someone asks why winter heating wasn't in the price, this sheet is your answer.`,
    },
    {
      title: 'Import the standards',
      icon: '📚',
      speech: `L G L keeps its standard clarifications in the shared library. Start every estimate by importing the applicable ones, then edit the wording to fit this project and add anything scope-specific. Editing your copies never changes the library, so tailor away.`,
    },
    {
      title: 'Keep them current',
      icon: '🔄',
      speech: `Treat this sheet as living text while the estimate develops — every assumption the team makes in a discipline sheet deserves a line here. When the proposal goes out, this list rides along and protects the price. Short sheet, big shield.`,
    },
  ],

  'cb-sov-help': [
    {
      title: 'Speaking the client\'s language',
      icon: '🗂️',
      turns: [
        { speaker: 'clara', speech: `The schedule of values maps your estimate onto the client's own work breakdown structure — their codes, their pay lines.` },
        { speaker: 'liam', speech: `You built the estimate your way; this sheet teaches it to speak the client's language.` },
      ],
    },
    {
      title: 'Codes that connect',
      icon: '🔗',
      turns: [
        { speaker: 'clara', speech: `Build the client W B S lines here, then tag Detail and Summary rows with matching S O V codes.` },
        { speaker: 'liam', speech: `Sync from detail rolls labour, material, subs, and rentals onto those codes — the same idea as Excel sum-if — so billing can reconcile after award.` },
      ],
    },
    {
      title: 'Do it early',
      icon: '⏰',
      turns: [
        { speaker: 'clara', speech: `Set the S O V structure up early, not the night the bid closes.` },
        { speaker: 'liam', speech: `When codes exist from the start, the team tags as they estimate, and the final mapping is a formality instead of a scramble.` },
      ],
    },
  ],

  // -------------------------------------------------------------------------
  // Standalone estimation tools
  // -------------------------------------------------------------------------

  'mto-log-help': [
    {
      title: 'What is the MTO Log?',
      icon: '📐',
      turns: [
        { speaker: 'clara', speech: `Ever wondered where every pipe, fitting, and flange on a piping estimate actually lives? That is the M T O log — the material takeoff for the whole job.` },
        { speaker: 'liam', speech: `Right. Every line the client sends comes in here, gets hours and prices, and eventually flows into the field tracker. Busiest sheet in the module — so let's walk it one idea at a time.` },
      ],
    },
    {
      title: 'Matching against the Part Rate database',
      icon: '🧱',
      turns: [
        { speaker: 'clara', speech: `When you import a file, every row is matched against the Part Rate database using five columns — type, material, size, schedule, and rating.` },
        { speaker: 'liam', speech: `A successful match pulls in the part number, labour hours per each, joints, joint type, diameter inches per each, and the unit price. Rows that don't match are not imported.` },
        { speaker: 'clara', speech: `Instead, the uploader gets an email listing the misses, so those parts can be added to the catalogue first and the file re-uploaded. Strict on purpose — that is how the M T O stays clean.` },
      ],
    },
    {
      title: 'The math behind the columns',
      icon: '🧮',
      turns: [
        { speaker: 'liam', speech: `Most of the money columns are calculated for you. Total hours is quantity times hours per each. Material cost is quantity times unit price. Labour plus material is the grand total.` },
        { speaker: 'clara', speech: `Every hour and cost then splits into shop and field using the estimate's shop to field ratio, each priced at its own labour rate. Factors multiply labour only — never material.` },
        { speaker: 'liam', speech: `And the U S dollar columns stay blank until the project manager freezes a conversion rate, so nobody prices off a stale exchange rate by accident.` },
      ],
    },
    {
      title: 'Purchasing, Part Rate, and the M T O',
      icon: '🛒',
      turns: [
        { speaker: 'clara', speech: `Here is the pricing loop everyone needs to know. From the M T O, the technical coordinator opens send to purchasing and checks which categories to send — valves, carbon steel, and so on.` },
        { speaker: 'liam', speech: `Purchasing gets one Excel file per checked category. You can also tick individual lines in the preview. The unit column is the M T O unit of measure — pipe defaults to feet when it is blank.` },
        { speaker: 'clara', speech: `They fill the blank price column and send the files back. Then you go to the Part Rate database and run import cost.` },
        { speaker: 'liam', speech: `That overwrites each part's cost, keeps the history, and pushes the price onto matching M T O rows so material totals recompute. If the same part number shows up with more than one different cost in the file, those parts are skipped and listed so you can fix and re-import.` },
        { speaker: 'clara', speech: `Until a cost is imported for a part, unit price on the M T O stays blank or zero. Specs can ride along in the send email for purchasing to download.` },
      ],
    },
    {
      title: 'Analytics at a glance',
      icon: '📊',
      turns: [
        { speaker: 'liam', speech: `Analytics summarises the loaded takeoff — shop and field cards, total feet for pipe families, pipe by size, and diameter inches by joint type.` },
        { speaker: 'clara', speech: `The shop to field ratio on those cards comes from Factors, and you can edit it on the overview and save it back. Change history lives there too when revisions move the scope.` },
      ],
    },
    {
      title: 'Revisions and after the award',
      icon: '🔁',
      turns: [
        { speaker: 'liam', speech: `Uploads arrive two ways. A full Excel from the client becomes a client revision — revision zero is the bid. Isometric batches append through the job, and the latest version of each drawing wins.` },
        { speaker: 'clara', speech: `Once the estimate is awarded, uploads roll budget hours into the piping tracker without erasing field progress. When an ISO is re-issued with a new version, the old tracker line is struck through and kept for history, and a new active line is added with the new quantities.` },
        { speaker: 'liam', speech: `If that old line already had shop or field progress, the new active line goes on hold, and the system opens two automatic change orders — one for the scope and quantity differences, and a second one that claims the earned hours from the struck-through line so they are not lost when live reports exclude superseded rows.` },
        { speaker: 'clara', speech: `So the rhythm is import, match, send to purchasing, import cost, revise, and track. Take it one revision at a time and the sheet takes care of the rest!` },
      ],
    },
  ],

  'bom-extractor-help': [
    {
      title: 'From drawing to spreadsheet',
      icon: '📄',
      speech: `Picture a stack of P D F isometric drawings, each with a bill of materials table you'd normally retype by hand. The B O M extractor does that typing for you: it reads the drawing with A I and hands you back a clean, structured Excel file. What used to be an afternoon of squinting becomes a couple of clicks.`,
    },
    {
      title: 'The basic flow',
      icon: '⚙️',
      speech: `The flow is simple. First, upload a P D F drawing — up to fifty megabytes. Then pick your options: the unit system, imperial or metric, the drawing format, and the file type. Hit extract, let the A I process the pages, and when it's done, download the results as a spreadsheet. That's the whole happy path.`,
    },
    {
      title: 'Teaching it with plain language',
      icon: '🤖',
      speech: `Here's the clever part: when the extraction isn't quite right, you don't edit code — you just tell the assistant on the right what's wrong, in plain English. Say the table is cut off on the right, and it widens the table region. Say it's confusing quantity with size, and it adjusts the instructions it gives the A I. You can even paste a screenshot straight into the chat to show it exactly what you mean.`,
    },
    {
      title: 'Formats and the Format Builder',
      icon: '📐',
      speech: `Behind the scenes, each drawing type has a format: the regions to read on the page, the fields to pull out, the prompts the A I follows, and how it all maps into the spreadsheet. The Format Builder is where formats are created and tuned — upload a sample P D F, draw the regions visually, test the extraction, and see the results instantly. Duplicating an existing format is the easiest way to start a new one.`,
    },
    {
      title: 'Who can do what',
      icon: '🔐',
      speech: `One thing to know: this page is limited to a small group of four authorized users, and formats have owners — you can always edit the ones you created, and duplicating someone else's format gives you your own copy to change freely. If the tool tells you a format is read only, that's why. Happy extracting!`,
    },
  ],

  'scheduling-estimator-help': [
    {
      title: 'How much scheduling does a job need?',
      icon: '🗓️',
      speech: `How many hours of scheduling effort does a project actually need? Guess too low and the schedule goes stale; guess too high and you've priced yourself out. This estimator answers that question two ways — a quick rule of thumb, and a detailed method that weighs the project's real complexity.`,
    },
    {
      title: 'The rule of thumb',
      icon: '⚡',
      speech: `The quick method is beautifully simple: one and a half hours per week for every million dollars of project value, subject to minimum hours by size tier — small projects still get at least eight hours a week, and the minimums climb from there. It covers schedule development, updates, progress tracking, reporting, and coordination meetings. Reach for it early: feasibility studies, budgeting, proposals, and sanity checks against a detailed number.`,
    },
    {
      title: 'The detailed complexity method',
      icon: '🔬',
      speech: `When the job is awarded and you need a real staffing plan, switch to the detailed method. It starts from the same base and then layers in what actually drives scheduling effort: a complexity multiplier, extra weekly hours for each work area, each discipline, and each subcontractor, and a scale factor for labour intensive jobs. And here's the one people miss — the shutdown flag. Shutdowns and turnarounds apply a one and a half times multiplier plus a coordination premium, because compressed timelines and six or seven day weeks demand far more intensive scheduling.`,
    },
    {
      title: 'Being there matters',
      icon: '📍',
      speech: `Hours alone aren't the whole story — the scheduler also has to show up. The site presence guidelines scale with project value: about one day a week on a small job, up to full time, six days a week, with an assistant, on the largest ones. Treat these as minimums; shutdowns, multi phase work, and congested sites usually need more.`,
    },
    {
      title: 'Grounded in the standards',
      icon: '📚',
      speech: `None of this is invented here — the methods lean on recognized industry references like the A A C E estimate classification system, R S Means productivity data, and the P M I scheduling practice standard. So when someone challenges your scheduling line, you have something to point at. Estimate with confidence!`,
    },
  ],

  'mech-equipment-history-help': [
    {
      title: 'Twenty years of mechanical estimates, one table',
      icon: '⚙️',
      turns: [
        { speaker: 'clara', speech: `Piping has the Part Rate catalogue. Mechanical needed something richer — real equipment we have already bid.` },
        { speaker: 'liam', speech: `This database is filled from SharePoint Estimates and Project History: pumps, vessels, dryers, conveyors, skids, and more. One row per historical instance, with the specs and estimated hours we could extract.` },
      ],
    },
    {
      title: 'Sources are the safety net',
      icon: '🔗',
      turns: [
        { speaker: 'clara', speech: `Every line remembers the exact SharePoint file — and when we know it, the sheet and row. Open the source if height or another field is missing.` },
        { speaker: 'liam', speech: `That is deliberate. Extraction will never be perfect on twenty-year-old scans, so the address is how estimators and the crawler both come back to fill gaps.` },
      ],
    },
    {
      title: 'Find something eighty percent similar',
      icon: '🎯',
      turns: [
        { speaker: 'liam', speech: `Starting a new pump? Enter type, size, horsepower, weight, material — whatever you know.` },
        { speaker: 'clara', speech: `Fuzzy match returns peers with a similarity score and a suggested install-hour range: twenty-fifth, median, and seventy-fifth percentile. Rough, but far better than a blank sheet.` },
      ],
    },
    {
      title: 'Estimated now, Maestro later',
      icon: '📈',
      turns: [
        { speaker: 'clara', speech: `Hours and costs here are from historical estimates. Actual hours from Maestro are planned for a later phase — the columns exist, but stay empty until that connection is approved.` },
        { speaker: 'liam', speech: `A nightly crawl keeps adding files. Use deep crawl for a big first fill. Keep estimating — and let history do some of the heavy lifting!` },
      ],
    },
  ],

  'part-rate-db-help': [
    {
      title: 'The catalogue behind the MTO',
      icon: '🧱',
      turns: [
        { speaker: 'clara', speech: `Ever wonder where the M T O gets its part numbers, hours, and prices? Right here.` },
        { speaker: 'liam', speech: `The Part Rate database is the reference catalogue — labour hours and purchase cost for every part. Every line uploaded to the M T O is matched against this page, so the quality here is the quality of every piping estimate.` },
      ],
    },
    {
      title: 'How matching works',
      icon: '🔗',
      turns: [
        { speaker: 'clara', speech: `Matching uses five columns — type, material, size, schedule, and rating. End type and seam type stay on the row for reference but are not part of the match.` },
        { speaker: 'liam', speech: `On a match, the M T O also picks up joints, joint type, and diameter inches per each. Total joints and total diameter inches on the takeoff are just those values times quantity.` },
        { speaker: 'clara', speech: `On the hours side, O T T A V hours are the single basis for every hour and labour calculation. M C A A figures live here too, but they are set aside for now and do not drive totals.` },
      ],
    },
    {
      title: 'Purchasing prices land here first',
      icon: '💲',
      turns: [
        { speaker: 'liam', speech: `The pricing loop starts on the M T O: send to purchasing emails one Excel file per category the coordinator checked.` },
        { speaker: 'clara', speech: `When the priced files come back, you import cost here. Each part's cost is overwritten, history is kept, and the price is pushed onto matching M T O rows automatically.` },
        { speaker: 'liam', speech: `If the same part number appears with more than one different cost in that file, those parts are skipped and you get a list — and a C S V — so you can fix the file and try again.` },
        { speaker: 'clara', speech: `Click any part number for its price trend over time. That is a quiet superpower when a supplier quote looks off.` },
      ],
    },
    {
      title: 'Growing it deliberately',
      icon: '🌱',
      turns: [
        { speaker: 'liam', speech: `When an M T O upload contains parts that are not in here, those rows are rejected and the uploader is emailed the missing list — add them here first, then re-upload.` },
        { speaker: 'clara', speech: `Whenever someone adds new parts, piping estimators are notified so a lead can review the rates. Slow and deliberate beats fast and messy. Keep it clean!` },
      ],
    },
  ],

  'rate-benchmarks-help': [
    {
      title: 'Five rate books, one table',
      icon: '📊',
      turns: [
        { speaker: 'clara', speech: `How do you know the hours in the Part Rate database are actually right? You check them against everything else on the shelf.` },
        { speaker: 'liam', speech: `That is exactly what this page does. It lines the Part Rate database up against four independent references — two Ottav rate books, the M C A A manual, and our own Estimating Book, sections six and seven — every part, every size, side by side.` },
        { speaker: 'clara', speech: `All four workbooks were imported and translated into one shared vocabulary — same descriptions, same materials, same schedules, same sizes — so a two inch weld neck flange in one book lands on the same row as a two inch weld neck flange in another.` },
      ],
    },
    {
      title: 'Reading the comparison',
      icon: '🔍',
      turns: [
        { speaker: 'liam', speech: `In the Comparison Hub, every row is one part at one size. A number means that source has an hour value. A yellow gap badge means the source knows the part family but is missing this size — a data entry hole worth a look.` },
        { speaker: 'clara', speech: `And a plain dash means the source simply does not carry that part at all. Those three states are kept honest on purpose — a missing number never quietly becomes a zero.` },
        { speaker: 'liam', speech: `The little letter f marks values derived from an Estimating Book factor, and the coloured percentage badges compare each benchmark against its own fair counterpart — Ottav and the Estimating Book against the O T T hour column, and the M C A A book against the Part Rate database's own M C A A column.` },
        { speaker: 'clara', speech: `Sort by biggest deviation and the rows most worth investigating float straight to the top.` },
      ],
    },
    {
      title: 'The AI reads it with you',
      icon: '🤖',
      turns: [
        { speaker: 'clara', speech: `At the top of the hub there is a paragraph written by the portal's A I. It reads the aggregate statistics of whatever filter you have on — not the raw tables — and tells you the story: which source runs hot, which families disagree the most, where the coverage holes are.` },
        { speaker: 'liam', speech: `Change any filter and it re-reads the picture. Filter down to stainless flanges and it talks about stainless flanges. It is like having a second estimator glance over your shoulder at every slice.` },
      ],
    },
    {
      title: 'Factors and the Estimating Book',
      icon: '📚',
      turns: [
        { speaker: 'liam', speech: `The Estimating Book tab shows every sheet from sections six and seven — the base table plus its multiplier factors, like schedule eighty or stainless steel.` },
        { speaker: 'clara', speech: `When a factor is a range, both ends are shown and the average sits beside them as the number the calculations actually use. Authorized users can edit factors or add new ones, and every change is written to the audit log.` },
        { speaker: 'liam', speech: `Edit a factor and the derived rows in the Comparison Hub update right away — so you can test what a factor change would do to the whole comparison before anyone touches the Part Rate database itself.` },
      ],
    },
    {
      title: 'Unmatched parts and the crosswalk',
      icon: '🧭',
      turns: [
        { speaker: 'clara', speech: `The unmatched tab is the tidy-up list: part families that exist in a benchmark but never join a Part Rate database row, and vice versa.` },
        { speaker: 'liam', speech: `Often it is just spelling — one book says conc swage, another says concentric swage. Add a crosswalk mapping and the join recomputes on the spot.` },
        { speaker: 'clara', speech: `What is left after the spelling fixes is the real gold — genuine coverage gaps, your shopping list for growing the Part Rate database. Happy benchmarking!` },
      ],
    },
  ],

  'pid-extraction-help': [
    {
      title: 'Reading the drawings for you',
      icon: '🔎',
      speech: `Pulling line numbers and tags off a stack of P and I D drawings by hand is slow, squint inducing work. This tool does it for you: describe what a tag looks like, upload the drawings, and it finds every match. Three steps — patterns, upload, scan.`,
    },
    {
      title: 'Describing a tag with wildcards',
      icon: '🧩',
      speech: `A pattern is just a template of your tag format. A zero stands for any digit, a capital X stands for any letter, and everything else — dashes, quote marks — must match exactly. So a pattern of four zeros, a dash, two X's, a dash, and two more zeros would catch a tag like twelve thirty four, A B, fifty six. You can add several patterns to one scan, and the shared pattern library lets you save yours and load the team's — loading more than one set just combines them without duplicates.`,
    },
    {
      title: 'Two ways to scan',
      icon: '⚙️',
      speech: `After you drag in one or more P D F files, you pick the scanning mode, and this choice matters. If the P D F has selectable text, use searchable mode — it reads the text straight out of the file, instantly and very reliably. If the drawings are scans or images, switch to the A I vision mode: it reads each page visually, which takes thirty to ninety seconds per page, but it can handle any image.`,
    },
    {
      title: 'Take the results with you',
      icon: '📤',
      speech: `When the scan finishes, you get every matched tag, ready to export to Excel for whatever comes next — takeoffs, line lists, checking against the client's index. Save your patterns as you go and the next batch of drawings will take minutes. Happy tag hunting!`,
    },
  ],

  'labour-rates-bank-help': [
    {
      title: 'One home for every rate',
      icon: '💰',
      speech: `Where does an estimator find the right labour rate — and how do you know everyone is using the same one? That's the job of the Labour Rates Bank: one centralized repository for all the labour rate data used in estimation, instead of rates scattered across old spreadsheets.`,
    },
    {
      title: 'Organized by category',
      icon: '🗂️',
      speech: `The rates are consolidated into categories, and each category has its own tab with the columns that make sense for it. Click a tab to switch category, and use the division and project filters at the top to narrow the list down to exactly the rates that apply to your situation.`,
    },
    {
      title: 'Keeping it current',
      icon: '✏️',
      speech: `Every rate supports inline editing — click in, type the new number, done. Since the whole estimating team draws from this one bank, a rate corrected here is corrected for everyone. Keep it fresh, and every estimate downstream gets better. That's the whole deal!`,
    },
  ],

  'rentals-help': [
    {
      title: 'The rentals catalogue',
      icon: '🏗️',
      turns: [
        { speaker: 'clara', speech: `Cranes, man lifts, welders, generators — almost every job rents equipment, and this sheet is the cost catalogue.` },
        { speaker: 'liam', speech: `Thirty-one equipment families, from lifting gear through confined space and fall protection — the reference estimators price from.` },
      ],
    },
    {
      title: 'Pull into the estimate',
      icon: '🔗',
      turns: [
        { speaker: 'clara', speech: `With an estimate selected, pull rentals into the estimate writes Outside Rentals lines on the matching discipline summaries.` },
        { speaker: 'liam', speech: `Site-wide gear can land on site expenses. Anything unmapped goes to Indirects so nothing silent-drops. Re-pull updates the tagged lines instead of stacking duplicates.` },
      ],
    },
    {
      title: 'It works like a spreadsheet',
      icon: '⌨️',
      turns: [
        { speaker: 'clara', speech: `Click a cell, edit, and it saves when you leave the cell. Copy and paste works with Excel both ways.` },
        { speaker: 'liam', speech: `Blue header rows mark equipment families. Keep sales taxes and duties in the discipline rental totals, and note currency on shop rentals in the comments.` },
      ],
    },
    {
      title: 'Why Summary rentals can exceed Detail',
      icon: '🧠',
      turns: [
        { speaker: 'clara', speech: `Detail often only carries miscellaneous rental dollars on takeoff rows. The big crane packages usually live on Summary or arrive from this catalogue pull.` },
        { speaker: 'liam', speech: `That is why a real job can show a small Detail rental total and a much larger Outside Rentals total on Summary — and still be correct. Rent wisely!` },
      ],
    },
  ],

  'cb-dashboard-help': [
    {
      title: 'Your estimate at a glance',
      icon: '📍',
      turns: [
        { speaker: 'clara', speech: `The Estimation Dashboard is the status board for the estimate you have open — completeness, cost mix, and shortcuts into the sheets that still need work.` },
        { speaker: 'liam', speech: `It does not replace Detail or Summary. It tells you whether the pyramid is filled in before you chase a number on the one-pager.` },
      ],
    },
    {
      title: 'What good looks like',
      icon: '✅',
      turns: [
        { speaker: 'clara', speech: `Detail rows exist for the disciplines you bid. Summaries have Auto rows from rollup plus staff and supervision where the job needs them.` },
        { speaker: 'liam', speech: `Indirects, site, and rentals are not blank if the scope needs them. Crew hours match summary labour. S O V codes are tagged when the client structure is known.` },
      ],
    },
    {
      title: 'Jump and fix',
      icon: '🚀',
      turns: [
        { speaker: 'clara', speech: `Use the shortcuts to open the thin spots, fill them, and come back.` },
        { speaker: 'liam', speech: `When the board looks healthy, the C B Summary and bid package are ready for a real review. That is the whole idea!` },
      ],
    },
  ],

  'cb-bid-form-help': [
    {
      title: 'Bid form from live numbers',
      icon: '📝',
      turns: [
        { speaker: 'clara', speech: `This bid form is not a blank Word template. It pulls selling price, cost buckets, and hours straight from the C B one-pager for the estimate you have open.` },
        { speaker: 'liam', speech: `That means the numbers you just built in Detail and Summary are the same numbers the client-facing form starts from.` },
      ],
    },
    {
      title: 'Edit, copy, regenerate',
      icon: '✏️',
      turns: [
        { speaker: 'clara', speech: `Polish the draft in place, then copy it out when you are ready.` },
        { speaker: 'liam', speech: `Your edits stay in this browser until you regenerate. If the sheets underneath move, regenerate so the form catches up.` },
      ],
    },
  ],

  'cb-proposal-letter-help': [
    {
      title: 'Proposal letter',
      icon: '📨',
      turns: [
        { speaker: 'clara', speech: `The proposal letter starts from the same live selling price and hours, written as a short client-facing note.` },
        { speaker: 'liam', speech: `It is a starting draft, not a locked legal document — you still own the wording for the opportunity.` },
      ],
    },
    {
      title: 'Polish and send',
      icon: '✉️',
      turns: [
        { speaker: 'clara', speech: `Tune the tone, then copy into Outlook or your proposal pack.` },
        { speaker: 'liam', speech: `If the estimate totals move, regenerate so the letter does not quote yesterday's price.` },
      ],
    },
  ],

  'cb-clarification-letter-help': [
    {
      title: 'Clarification letter',
      icon: '💬',
      turns: [
        { speaker: 'clara', speech: `Clarifications you typed on the Clarifications sheet become a numbered letter here.` },
        { speaker: 'liam', speech: `Keep the source list honest first — the letter is just the readable packaging.` },
      ],
    },
    {
      title: 'Refresh from the sheet',
      icon: '🔄',
      turns: [
        { speaker: 'clara', speech: `Add or edit clarifications on that sheet, then refresh this letter.` },
        { speaker: 'liam', speech: `Copy the result into the bid package when the list looks complete.` },
      ],
    },
  ],

  // -------------------------------------------------------------------------
  // SharePoint (project Doc's Mngmnt + Estimation SharePoint page)
  // -------------------------------------------------------------------------

  'sharepoint-browser-help': [
    {
      title: 'Stay in the portal',
      icon: '📂',
      turns: [
        { speaker: 'clara', speech: `SharePoint used to mean leave the portal, dig through folders, then come back. Now the common path stays here.` },
        { speaker: 'liam', speech: `Open SharePoint from the project or estimate sidebar, walk the folders, preview a P D F or photo, and download what you need — without bouncing to another site for every click.` },
      ],
    },
    {
      title: 'When to leave for Microsoft three sixty five',
      icon: '↗',
      turns: [
        { speaker: 'clara', speech: `Editing Word or Excel, uploading a stack of files, or changing permissions still belongs in SharePoint itself.` },
        { speaker: 'liam', speech: `Hit Open in SharePoint for that. Co-authoring and library admin stay where Microsoft does them best — we are not pretending to replace the whole SharePoint app inside an iframe.` },
      ],
    },
    {
      title: 'Star a folder while you browse',
      icon: '⭐',
      turns: [
        { speaker: 'clara', speech: `When you are already looking at the right folder, you should not have to copy the address and paste it into a form.` },
        { speaker: 'liam', speech: `Hit the star to the left of Open. That saves the folder as a personal bookmark. Hit the star again and it comes off your list.` },
      ],
    },
    {
      title: 'Personal or team bookmarks',
      icon: '🔖',
      turns: [
        { speaker: 'clara', speech: `Paste a deep folder link, give it a label, and it starts as yours alone. When you want to share, pick teammates from a list — or everyone on the job — instead of blasting the whole crew by default.` },
        { speaker: 'liam', speech: `They get an invite and must accept before it shows on their SharePoint page. Refuse keeps it off their list. Export and import J SON to back up or copy bookmarks; only the owner or an admin can edit or remove yours.` },
      ],
    },
    {
      title: 'Job site versus estimate folder',
      icon: '🧭',
      turns: [
        { speaker: 'clara', speech: `On a project, the root is the job SharePoint site from Costing. On an estimate, the root is that estimate's folder under Active Estimates.` },
        { speaker: 'liam', speech: `Those two are never swapped. If a job has no site yet, you will see the empty root message — do not expect the estimate library to appear on the project page.` },
      ],
    },
  ],

  'autodesk-build-help': [
    {
      title: 'A bridge, not a copy of Autodesk',
      icon: '🏗️',
      turns: [
        { speaker: 'clara', speech: `Autodesk Build stays where the drawings, issues, and R F I live. This page is the same idea as SharePoint — a door from the portal, not a second Autodesk.` },
        { speaker: 'liam', speech: `Paste the project Home link from A C C once. We line up Home, Files, Sheets, Issues, R F I, and the other Build tools so anyone on the job can jump there.` },
      ],
    },
    {
      title: 'Open or bookmark',
      icon: '⭐',
      turns: [
        { speaker: 'clara', speech: `Open sends you to Autodesk in a new tab, which is where you still edit and mark up.` },
        { speaker: 'liam', speech: `The star next to Open keeps that tool on your personal list. Share works the same as SharePoint bookmarks — invite, then they accept.` },
      ],
    },
    {
      title: 'Job and estimate stay separate',
      icon: '🧭',
      turns: [
        { speaker: 'clara', speech: `A construction job and an estimate each get their own Autodesk link. Do not expect one to appear on the other page.` },
        { speaker: 'liam', speech: `If nobody has pasted a project U R L yet, save it from Autodesk Build Home and the tool list fills in.` },
      ],
    },
  ],

  'procore-help': [
    {
      title: 'A bridge, not a copy of Procore',
      icon: '🚧',
      turns: [
        { speaker: 'clara', speech: `When the client lives in Procore, this page is the door. R F I, drawings, documents, photos — open them there. We do not colour their model viewer.` },
        { speaker: 'liam', speech: `Paste the project Home link from Procore once. We line up Home, Documents, Drawings, R F I, and the other tools so anyone on the job can jump there.` },
      ],
    },
    {
      title: 'Open or bookmark',
      icon: '⭐',
      turns: [
        { speaker: 'clara', speech: `Open sends you to Procore in a new tab, which is where you still write the R F I and mark up the drawing.` },
        { speaker: 'liam', speech: `The star next to Open keeps that tool on your personal list. Share works the same as SharePoint bookmarks — invite, then they accept.` },
      ],
    },
    {
      title: 'Colour stays on Schedule Model',
      icon: '🎨',
      turns: [
        { speaker: 'clara', speech: `Progress colour lives under Schedule, Model, from live P six. This page never promised to paint Procore.` },
        { speaker: 'liam', speech: `A construction job and an estimate each get their own Procore link. Do not expect one to appear on the other page.` },
      ],
    },
  ],
};
